import { create } from "zustand";
import { combine, persist, createJSONStorage } from "zustand/middleware";
import type { Collection } from "@/lib/client/favorites";
import {
  collectionsService,
  sortCollectionsByOrder,
  collectionExists,
} from "@/services/collectionsService";
import { logError } from "@/lib/client/errors";

/**
 * Result type for optimistic add operations.
 * Indicates success, optional error message, and whether collection is at capacity.
 */
interface OptimisticAddResult {
  success: boolean;
  error?: string;
  isCollectionFull?: boolean;
  favoriteId?: number;
}

/**
 * Result type for optimistic remove operations.
 * Indicates success and optional error message.
 */
interface OptimisticRemoveResult {
  success: boolean;
  error?: string;
}

/**
 * Initial state for the collections store.
 * Contains all collections, active collection data, loading states,
 * pagination info, optimistic update tracking, and user-scoped data.
 */
const initialState = {
  /** All user collections */
  collections: [] as Collection[],
  /** ID of the currently active collection */
  activeCollectionId: null as number | null,
  /** Set of item IDs in the active collection (for fast lookups) */
  activeCollectionItemIds: new Set<number>(),
  /** Items in the active collection (for rendering) */
  activeCollectionItems: [] as SearchResultItem[],
  /** Loading state for collections list */
  collectionsLoading: false,
  /** Loading state for collection items */
  itemsLoading: false,
  /** Loading state for pagination (load more) */
  loadingMore: false,
  /** Whether more items are available to load */
  hasMore: true,
  /** Number of items to display in active collection */
  displayedItemsCount: 20,
  /** Optimistic count changes for favorite counts (item ID -> delta) */
  optimisticCountChanges: new Map<number, number>(),
  /** Whether there are pending optimistic changes */
  hasPendingChanges: false,
  /** Cache of collection items (collection ID -> items array) */
  collectionItemsCache: {} as Record<number, SearchResultItem[]>,
  /** Map of all collection item IDs (collection ID -> Set of item IDs) */
  allCollectionItemIds: new Map<number, Set<number>>(),
  /** Current authenticated user ID (for localStorage scoping) */
  currentUserId: null as string | null,
  /** Cache of "liked by" info for items (item ID -> {total_users, last_username}) */
  itemLikedByInfo: new Map<number, { total_users: number; last_username: string | null }>(),
};

// Safe localStorage wrapper
const isClient = typeof window !== "undefined";

/**
 * Safe localStorage wrapper that handles SSR and potential storage errors.
 * All operations are no-ops on server and log errors on failure.
 */
const safeLocalStorage = {
  getItem: (key: string): string | null => {
    if (!isClient) return null;
    try {
      return localStorage.getItem(key);
    } catch (error) {
      logError(error as Error, { context: "localStorage.getItem" });
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    if (!isClient) return;
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      logError(error as Error, { context: "localStorage.setItem" });
    }
  },
  removeItem: (key: string): void => {
    if (!isClient) return;
    try {
      localStorage.removeItem(key);
    } catch (error) {
      logError(error as Error, { context: "localStorage.removeItem" });
    }
  },
};

/**
 * Helper to get user-scoped storage key.
 * Ensures each user's collections are stored separately in localStorage.
 *
 * @param userId - The user ID to scope the key for, or null for anonymous
 * @returns Storage key for the user's collections
 */
const getUserStorageKey = (userId: string | null) => {
  return userId ? `collections-storage-${userId}` : "collections-storage-anonymous";
};

/**
 * Collections store manages user favorites/collections.
 *
 * Features:
 * - Manages collections list and active collection
 * - Optimistic add/remove operations with rollback on error
 * - Collection items caching for fast switching
 * - User-scoped localStorage persistence
 * - "Liked by" data tracking per item
 *
 * Persistence:
 * - Active collection ID and items cache are persisted to localStorage
 * - Storage is user-scoped to prevent data leakage between users
 *
 * @example
 * ```ts
 * const { actions } = useCollectionsStore();
 * await actions.loadCollections();
 * await actions.optimisticallyAddItem(itemId, item);
 * ```
 */
export const useCollectionsStore = create(
  persist(
    combine(initialState, (set, get) => {
      const actions = {
        /**
         * Sets the collections list and ensures a valid active collection.
         * Sorts collections by order and auto-selects the first collection if needed.
         *
         * @param collections - Array of collections to set
         */
        setCollections: (collections: Collection[]) => {
          const sortedCollections = sortCollectionsByOrder(collections);
          set({ collections: sortedCollections });

          const { activeCollectionId } = get();

          // Check if persisted activeCollectionId still exists in loaded collections
          const activeCollectionExists = activeCollectionId
            ? collectionExists(sortedCollections, activeCollectionId)
            : false;

          // If no active collection, or if the persisted one no longer exists, select the first
          if ((!activeCollectionId || !activeCollectionExists) && sortedCollections.length > 0) {
            set({ activeCollectionId: sortedCollections[0].id });
          }
        },

        /** Sets the loading state for collections list */
        setCollectionsLoading: (loading: boolean) => set({ collectionsLoading: loading }),

        /** Sets the loading state for collection items */
        setItemsLoading: (loading: boolean) => set({ itemsLoading: loading }),

        /**
         * Sets the active collection by ID.
         *
         * @param collectionId - Collection ID to set as active, or null to deselect
         */
        setActiveCollectionId: (collectionId: number | null) => {
          set({ activeCollectionId: collectionId });
        },

        /**
         * Updates the active collection with items data.
         * Clears the pending changes flag and updates all related state.
         *
         * @param collectionId - The collection ID being activated
         * @param items - Items to populate the collection with
         */
        updateActiveCollectionData: (collectionId: number, items: SearchResultItem[]) => {
          const { allCollectionItemIds } = get();

          // Use all item IDs from the map, or fall back to items
          const allItemIds =
            allCollectionItemIds.get(collectionId) || new Set(items.map((item) => item.id));

          set({
            activeCollectionId: collectionId,
            activeCollectionItems: items,
            activeCollectionItemIds: allItemIds,
            hasPendingChanges: false,
          });
        },

        /**
         * Updates the active collection's item IDs.
         * Used when switching collections with pre-loaded ID data.
         *
         * @param collectionId - The collection ID being activated
         * @param itemIds - Array of item IDs to set
         */
        updateActiveCollectionItemIds: (collectionId: number, itemIds: number[]) => {
          const newItemIds = new Set(itemIds);

          set({
            activeCollectionId: collectionId,
            activeCollectionItemIds: newItemIds,
          });
        },

        /**
         * Adds an item to the active collection with optimistic UI updates.
         * Updates UI immediately, then calls API. Reverts on failure.
         *
         * @param itemId - The item ID to add
         * @param item - Full item object (optional, for immediate UI update)
         * @returns Promise resolving to success status and optional error/full status
         */
        optimisticallyAddItem: async (
          itemId: number,
          item?: SearchResultItem
        ): Promise<OptimisticAddResult> => {
          return await collectionsService.optimisticallyAddItem(
            itemId,
            item,
            () => {
              const state = get();
              return {
                activeCollectionItemIds: state.activeCollectionItemIds,
                activeCollectionItems: state.activeCollectionItems,
                activeCollectionId: state.activeCollectionId,
                collectionItemsCache: state.collectionItemsCache,
                allCollectionItemIds: state.allCollectionItemIds,
                collections: state.collections,
                optimisticCountChanges: state.optimisticCountChanges,
              };
            },
            (updates) => set(updates),
            actions.fetchItemLikedBy
          );
        },

        /**
         * Removes an item from the active collection with optimistic UI updates.
         * Updates UI immediately, then calls API. Reverts on failure.
         *
         * @param itemId - The item ID to remove
         * @returns Promise resolving to success status and optional error message
         */
        optimisticallyRemoveItem: async (itemId: number): Promise<OptimisticRemoveResult> => {
          return await collectionsService.optimisticallyRemoveItem(
            itemId,
            () => {
              const state = get();
              return {
                activeCollectionItemIds: state.activeCollectionItemIds,
                activeCollectionItems: state.activeCollectionItems,
                activeCollectionId: state.activeCollectionId,
                collectionItemsCache: state.collectionItemsCache,
                allCollectionItemIds: state.allCollectionItemIds,
                collections: state.collections,
                optimisticCountChanges: state.optimisticCountChanges,
              };
            },
            (updates) => set(updates),
            actions.loadCollectionItems,
            actions.fetchItemLikedBy
          );
        },

        /**
         * Checks if an item is in the active collection.
         *
         * @param itemId - The item ID to check
         * @returns true if item is in active collection, false otherwise
         */
        isItemInActiveCollection: (itemId: number): boolean => {
          const { activeCollectionItemIds } = get();
          return activeCollectionItemIds.has(itemId);
        },

        /**
         * Gets the latest favorite_count for an item from cache.
         * Searches through all cached collections to find the item.
         *
         * @param itemId - The item ID to search for
         * @returns The latest favorite_count, or null if not found in cache
         */
        getLatestItemCount: (itemId: number): number | null => {
          const { collectionItemsCache } = get();

          // Search through all cached collections to find the most recent favorite_count
          for (const items of Object.values(collectionItemsCache)) {
            const item = items.find((i) => i.id === itemId);
            if (item && item.favorite_count !== undefined) {
              return item.favorite_count;
            }
          }

          return null;
        },

        /**
         * Adds a new collection to the collections list.
         * Does not persist to API (use createCollection for that).
         *
         * @param collection - The collection to add
         */
        addCollection: (collection: Collection) => {
          const { collections } = get();
          set({ collections: [...collections, collection] });
        },

        /**
         * Removes a collection from the collections list.
         * If the removed collection was active, activates the first available collection.
         *
         * @param collectionId - The collection ID to remove
         */
        removeCollection: (collectionId: number) => {
          const { collections, activeCollectionId } = get();
          const newCollections = collections.filter((c) => c.id !== collectionId);
          set({ collections: newCollections });

          if (activeCollectionId === collectionId && newCollections.length > 0) {
            set({ activeCollectionId: newCollections[0].id });
          }
        },

        /**
         * Updates a collection locally (no API call).
         * Use for immediate UI updates before API confirmation.
         *
         * @param collectionId - The collection ID to update
         * @param updates - Partial collection object with fields to update
         */
        updateCollectionLocal: (collectionId: number, updates: Partial<Collection>) => {
          const { collections } = get();
          const newCollections = collections.map((c) =>
            c.id === collectionId ? { ...c, ...updates } : c
          );
          set({ collections: newCollections });
        },

        /**
         * Updates a collection via API and refreshes the collections list.
         *
         * @param collectionId - The collection ID to update
         * @param updates - Partial collection object with fields to update
         * @returns Promise resolving to success status and optional error message
         */
        updateCollection: async (collectionId: number, updates: Partial<Collection>) => {
          const result = await collectionsService.updateCollection(collectionId, updates);

          if (result.success) {
            actions.loadCollections();
          }

          return result;
        },

        /**
         * Updates the order of multiple collections via API.
         * Refreshes the collections list after successful update.
         *
         * @param orders - Array of {id, order} pairs specifying new order
         * @returns Promise resolving to success status and optional error message
         */
        updateCollectionOrders: async (orders: Array<{ id: number; order: number }>) => {
          const result = await collectionsService.updateCollectionOrders(orders);

          if (result.success) {
            await actions.loadCollections();
          }

          return result;
        },

        /**
         * Loads all collections for the authenticated user.
         * Sets the first collection as active if none is selected.
         * Also loads items for the active collection and preloads other collections.
         */
        loadCollections: async () => {
          actions.setCollectionsLoading(true);

          try {
            const collectionsData = await collectionsService.loadCollections();
            actions.setCollections(collectionsData);

            // Get the current state after setCollections (which may have set activeCollectionId)
            const { activeCollectionId, collections } = get();

            // Always ensure a collection is selected if collections exist
            if (collections.length > 0) {
              const collectionToLoad = activeCollectionId || collections[0].id;

              // If no collection was active, set it now
              if (!activeCollectionId) {
                actions.setActiveCollectionId(collectionToLoad);
              }

              await actions.loadCollectionItems(collectionToLoad);
              actions.deferredPreloadAllCollections();
            }
          } catch (error) {
            logError(error as Error, { context: "loadCollections" });
          } finally {
            actions.setCollectionsLoading(false);
          }
        },

        /**
         * Loads items for a specific collection.
         * Uses cache if available, otherwise fetches from API.
         *
         * @param collectionId - The collection ID to load items for
         * @returns Promise that resolves when items are loaded
         */
        loadCollectionItems: async (collectionId: number) => {
          return await collectionsService.preloadCollectionItems(
            collectionId,
            () => {
              const state = get();
              return {
                collectionItemsCache: state.collectionItemsCache,
                allCollectionItemIds: state.allCollectionItemIds,
                activeCollectionId: state.activeCollectionId,
              };
            },
            (updates) => set(updates),
            actions.updateActiveCollectionData,
            actions.setItemsLoading
          );
        },

        /**
         * Loads items for a public collection (no authentication required).
         * Uses cache if available, otherwise fetches from API.
         *
         * @param collectionId - The public collection ID to load items for
         * @returns Promise that resolves when items are loaded
         */
        loadPublicCollectionItems: async (collectionId: number) => {
          return await collectionsService.preloadPublicCollectionItems(
            collectionId,
            () => {
              const state = get();
              return {
                collectionItemsCache: state.collectionItemsCache,
                allCollectionItemIds: state.allCollectionItemIds,
                activeCollectionId: state.activeCollectionId,
              };
            },
            (updates) => set(updates),
            actions.updateActiveCollectionData,
            actions.setItemsLoading
          );
        },

        /**
         * Increases the number of displayed items by 20 (load more pagination).
         * No-op if already loading or no more items available.
         */
        loadMoreDisplayedItems: () => {
          const { displayedItemsCount, activeCollectionItems, loadingMore } = get();

          if (loadingMore) return;

          set({ loadingMore: true });

          const { newCount, hasMore } = collectionsService.calculateLoadMoreCount(
            displayedItemsCount,
            activeCollectionItems.length,
            20
          );

          set({
            displayedItemsCount: newCount,
            hasMore,
            loadingMore: false,
          });
        },

        /**
         * Preloads items for all collections except the active one.
         * Populates the collection items cache for faster switching.
         */
        preloadAllCollections: async () => {
          const { collections, activeCollectionId } = get();

          try {
            const cache = await collectionsService.preloadOtherCollections(
              collections,
              activeCollectionId
            );

            const { collectionItemsCache } = get();
            const newCache = { ...collectionItemsCache, ...cache };
            set({ collectionItemsCache: newCache });
          } catch (error) {
            logError(error as Error, { context: "preloadAllCollections" });
          }
        },

        /**
         * Schedules deferred preloading of all collections using requestIdleCallback.
         * Waits for page load to avoid blocking initial render.
         *
         * @returns Function to cancel the scheduled preload (if supported)
         */
        deferredPreloadAllCollections: () => {
          return collectionsService.deferredPreloadAllCollections(() => {
            const state = get();
            return {
              collections: state.collections,
              activeCollectionId: state.activeCollectionId,
            };
          }, actions.preloadAllCollections);
        },

        /**
         * Creates a new collection via API.
         * Sets the new collection as active and refreshes the collections list.
         *
         * @param collectionName - The name/title for the new collection
         * @param options - Optional configuration (appearance, metadata)
         * @returns Promise resolving to the created collection, or null on failure
         */
        createCollection: async (
          collectionName: string,
          options?: { appearance?: number; metadata?: Record<string, unknown> }
        ) => {
          try {
            const newCollection = await collectionsService.createCollection(
              collectionName,
              options
            );

            set((state) => ({
              collections: [...state.collections, newCollection],
              activeCollectionItems: [],
              activeCollectionItemIds: new Set(),
            }));

            if (newCollection.id) {
              actions.setActiveCollectionId(newCollection.id);
            }

            // Reload collections to ensure consistency across all components
            await actions.loadCollections();

            return newCollection;
          } catch (error) {
            logError(error as Error, { context: "createCollection" });
            return null;
          }
        },

        /**
         * Deletes a collection via API.
         * Removes it from the local state if successful.
         *
         * @param collectionId - The collection ID to delete
         * @returns Promise resolving to success status and optional error message
         */
        deleteCollection: async (collectionId: number) => {
          const result = await collectionsService.deleteCollection(collectionId);

          if (result.success) {
            actions.removeCollection(collectionId);
          }

          return result;
        },

        /** Resets the store to initial state */
        reset: () => set(initialState),

        /**
         * Sets the current user ID and clears data if user changed.
         * Also clears the previous user's localStorage data.
         *
         * @param userId - The new user ID, or null to logout
         */
        setUserId: (userId: string | null) => {
          const { currentUserId } = get();

          // If user changed, clear cache and reset for new user
          if (currentUserId !== userId) {
            // Clear old user's data from storage using safe wrapper
            if (currentUserId) {
              safeLocalStorage.removeItem(getUserStorageKey(currentUserId));
            }

            // Reset cache for new user
            set({
              currentUserId: userId,
              activeCollectionId: null,
              collectionItemsCache: {},
              collections: [],
              activeCollectionItems: [],
              activeCollectionItemIds: new Set(),
            });
          }
        },

        /**
         * Fetches "liked by" information for a specific item.
         * Caches the result in the store.
         *
         * @param itemId - The item ID to fetch "liked by" info for
         */
        fetchItemLikedBy: async (itemId: number) => {
          try {
            const result = await collectionsService.fetchItemLikedBy(itemId);

            if (result.success && result.data) {
              const { itemLikedByInfo } = get();
              const newLikedByInfo = new Map(itemLikedByInfo);
              newLikedByInfo.set(itemId, {
                total_users: result.data.total_users,
                last_username: result.data.last_username,
              });
              set({ itemLikedByInfo: newLikedByInfo });
            }
          } catch (error) {
            logError(error as Error, { context: "fetchItemLikedBy" });
          }
        },

        /**
         * Gets cached "liked by" information for an item.
         *
         * @param itemId - The item ID to get info for
         * @returns The cached info, or null if not cached
         */
        getItemLikedBy: (itemId: number) => {
          const { itemLikedByInfo } = get();
          return itemLikedByInfo.get(itemId) || null;
        },

        /**
         * Clears cached "liked by" information for an item.
         * Call this after optimistic updates to refresh the data.
         *
         * @param itemId - The item ID to clear cache for
         */
        clearItemLikedBy: (itemId: number) => {
          const { itemLikedByInfo } = get();
          const newLikedByInfo = new Map(itemLikedByInfo);
          newLikedByInfo.delete(itemId);
          set({ itemLikedByInfo: newLikedByInfo });
        },
      };

      return { actions };
    }),
    {
      name: "collections-storage",
      storage: createJSONStorage(() => ({
        getItem: (name: string) => {
          // Use safe wrapper
          const baseData = safeLocalStorage.getItem(name);
          if (baseData) {
            try {
              const parsed = JSON.parse(baseData);
              const userId = parsed.state?.currentUserId;
              if (userId) {
                // Load from user-specific storage
                const userKey = getUserStorageKey(userId);
                return safeLocalStorage.getItem(userKey) || baseData;
              }
            } catch {
              // Fall back to base data
            }
          }
          return baseData;
        },
        setItem: (name: string, value: string) => {
          // Always save to base storage using safe wrapper
          safeLocalStorage.setItem(name, value);

          // Also save to user-specific storage if we have a userId
          try {
            const parsed = JSON.parse(value);
            const userId = parsed.state?.currentUserId;
            if (userId) {
              const userKey = getUserStorageKey(userId);
              safeLocalStorage.setItem(userKey, value);
            }
          } catch {
            // Just use base storage
          }
        },
        removeItem: (name: string) => {
          safeLocalStorage.removeItem(name);
        },
      })),
      partialize: (state) => ({
        activeCollectionId: state.activeCollectionId,
        collectionItemsCache: state.collectionItemsCache,
        currentUserId: state.currentUserId,
      }),
    }
  )
);
